/**
 * src/services/chartApi.ts
 * ─────────────────────────────────────────────────────────────────────────────
 * The ONLY file that talks to Firestore directly.
 * All other code reaches Firestore through these functions.
 *
 * Collection path:
 *   charts/{userId}/userCharts/{chartId}
 *
 * Document shape:
 *   meta:        { userId, chartType, title, createdAt, updatedAt,
 *                  schemaVersion, generationSource }
 *   config:      ChartConfig  (buildLocalDraft() output minus data/annotations)
 *   data:        AnyDataPoint[]
 *   annotations: Annotation[]
 *   signals:     { exported, exportCount, iterated, abandoned, agentGenerated }
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  where,
  serverTimestamp,
  increment,
  CollectionReference,
  DocumentReference,
  DocumentData,
  Query,
} from 'firebase/firestore';
import { db } from '../firebase';
import type { ChartType, ChartConfig, AnyDataPoint, Annotation, ExportConfig } from '../types';
import type { LocalDraftConfig, BuildDraftResult } from './draftStorage';
import { quickValidateChartConfig, SCHEMA_VERSION } from '../types';

// ─────────────────────────────────────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────────────────────────────────────

export interface SavedChartMeta {
  chartId:          string;
  chartType:        ChartType | 'unknown';
  title:            string;
  updatedAt:        number;   // JS timestamp (ms)
  createdAt:        number;   // JS timestamp (ms)
  generationSource: GenerationSource;
}

export interface ChartSignals {
  exported:       boolean;
  exportCount:    number;
  iterated:       boolean;
  abandoned:      boolean;
  agentGenerated: boolean;
}

/** Shape stored in Firestore — meta wraps what would otherwise be top-level fields */
interface FirestoreChartDoc {
  meta: {
    userId:           string;
    chartType:        ChartType;
    title:            string;
    updatedAt:        ReturnType<typeof serverTimestamp>;
    createdAt?:       ReturnType<typeof serverTimestamp>;
    schemaVersion:    number;
    generationSource: string;
  };
  config:      Record<string, unknown>;
  data:        AnyDataPoint[];
  annotations: Annotation[];
  signals?:    ChartSignals;
}

/** What comes back from loadChart() — ready for applyLocalDraft() */
export interface LoadedChart extends Partial<ChartConfig> {
  chartType:    ChartType;
  data:         AnyDataPoint[];
  annotations:  Annotation[];
  _firestoreId: string;
  _meta:        FirestoreChartDoc['meta'] | undefined;
}

export type GenerationSource = 'human' | 'agent' | 'agent-edited';

// ─────────────────────────────────────────────────────────────────────────────
// COLLECTION HELPERS
// ─────────────────────────────────────────────────────────────────────────────

const userChartsCol = (userId: string): CollectionReference<DocumentData> =>
  collection(db, 'charts', userId, 'userCharts');

const chartDocRef = (userId: string, chartId: string): DocumentReference<DocumentData> =>
  doc(db, 'charts', userId, 'userCharts', chartId);

// ─────────────────────────────────────────────────────────────────────────────
// INTERNAL HELPERS
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Generates a unique human-readable document title.
 * Format: chartType_YYYY-MM-DD_HH-MM  (e.g. "bar_2026-03-11_14-35")
 */
function formatDocTitle(chartType: string): string {
  const d = new Date();
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${chartType}_${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}_${pad(d.getHours())}-${pad(d.getMinutes())}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// saveChart
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Creates or overwrites a chart document in Firestore.
 *
 * Accepts buildLocalDraft() output directly. Splits into Firestore shape
 * (meta + config + data + annotations + signals).
 *
 * - No chartId → creates a new document with an auto-generated ID.
 * - chartId provided → overwrites, preserving createdAt and signals via merge.
 *
 * Throws if quickValidateChartConfig fails.
 */
export async function saveChart(
  userId:            string,
  draft:             BuildDraftResult,
  chartId:           string | null = null,
  generationSource:  GenerationSource = 'human',
  saveCount:         number | undefined = undefined,
): Promise<{ chartId: string }> {

  // TODO(Sprint D): Consider upgrading to full validateChartConfig() from validateConfig.ts.
  // quickValidate is sufficient for now — catches structural issues without Zod strictness.
  const errors = quickValidateChartConfig(draft);
  if (errors) {
    throw new Error(`[chartApi] Invalid config before save:\n${errors.join('\n')}`);
  }

  // Destructure all fields — LocalDraftConfig extends ChartConfig so every
  // section is fully typed. Persistence-only fields discarded with _ prefix.
  const {
    data        = [],
    annotations = [],
    chartType,
    content,
    animation,
    background,
    layout,
    axes,
    legend,
    dataLabels,
    spacing,
    export:          exportRaw,   // renamed: 'export' is a reserved word
    _firestoreId:    _fid,
    _saveCount:      _sc,
    _sessionSavedAt: _ssa,
    isUsingDemoData: _iud,        // runtime flag — never written to Firestore
  } = draft;

  // Reconstruct ExportConfig explicitly so any runtime fields that leaked into
  // the export section (e.g. isUsingDemoData from old localStorage drafts)
  // are dropped before writing to Firestore. exportRaw is ChartConfig.export
  // so all three fields are guaranteed present — no optional chaining needed.
  const exportCfg: ExportConfig = {
    quality:      exportRaw.quality,
    isLooping:    exportRaw.isLooping,
    canvasFormat: exportRaw.canvasFormat,
  };

  // Assemble the Firestore config map. Every field is typed via ChartConfig.
  // axes is optional (absent on Pie) — store null rather than undefined so
  // Firestore doesn't drop the field entirely on merge writes.
  const config: ChartConfig = {
    chartType,
    content,
    animation,
    background,
    layout,
    ...(axes ? { axes } : {}),
    legend,
    dataLabels,
    spacing,
    export: exportCfg,
  };

  // Clean data array: remove noise fields before saving to Firestore.
  // AnyDataPoint is a discriminated union — we work within the union via
  // type-narrowing helpers so no casts are needed.
  const cleanData: AnyDataPoint[] = data.map((pt): AnyDataPoint => {
    // Step 1: strip nameOffsetX/Y from non-pie charts.
    // These are PieDataPoint-only fields that sometimes leak onto other types.
    // TODO(Sprint C): Generalize per-chartType data cleaning — each chart module
    // could export a cleanDataPoint() function, selected via chartType discriminant.
    // We spread into a typed base and conditionally omit via re-assignment.
    const base = { ...pt } as Record<string, unknown> & Pick<typeof pt, 'id' | 'chartType'>;
    if (chartType !== 'pie') {
      delete (base as Record<string, unknown>)['nameOffsetX'];
      delete (base as Record<string, unknown>)['nameOffsetY'];
    }
    // Step 2: remove empty styleOverride objects — noise with no rendering effect.
    const so = (base as { styleOverride?: unknown }).styleOverride;
    if (so !== null && so !== undefined && typeof so === 'object'
        && Object.keys(so).length === 0) {
      delete (base as Record<string, unknown>)['styleOverride'];
    }
    // base retains all real AnyDataPoint fields; only undefined optional noise removed.
    // base is a spread AnyDataPoint with only optional noise fields deleted.
    // Required fields are intact — the double-cast is justified here.
    return base as unknown as AnyDataPoint;
  });

  const title  = formatDocTitle(chartType as string);
  const ref    = chartId ? chartDocRef(userId, chartId) : doc(userChartsCol(userId));
  const isNew  = !chartId;

  interface SavePayload {
    meta:         Record<string, unknown>;
    config:       ChartConfig;
    data:         AnyDataPoint[];
    annotations:  Annotation[];
    signals?:     ChartSignals;
  }
  const payload: SavePayload = {
    meta: {
      userId,
      chartType,
      title,
      updatedAt:        serverTimestamp(),
      schemaVersion:    SCHEMA_VERSION,
      generationSource,
      ...(isNew && { createdAt: serverTimestamp() }),
    },
    config,
    data:        cleanData,
    annotations,
    ...(isNew && {
      signals: {
        exported:       false,
        exportCount:    0,
        iterated:       false,
        abandoned:      false,
        agentGenerated: generationSource === 'agent',
      } satisfies ChartSignals,
    }),
  };

  if (isNew) {
    await setDoc(ref, payload);
  } else {
    // Use updateDoc with the non-signals fields so we don't overwrite
    // the existing signals object (exported, exportCount, iterated etc.)
    // setDoc merge:true would replace the nested signals map entirely.
    const { signals: _s, ...payloadWithoutSignals } = payload;
    await setDoc(ref, payloadWithoutSignals, { merge: true });
    // Separately flip iterated=true if this is a 2nd+ save (caller passes saveCount)
    if (saveCount !== undefined && saveCount >= 2) {
      await updateDoc(ref, { 'signals.iterated': true });
    }
  }

  return { chartId: ref.id };
}

// ─────────────────────────────────────────────────────────────────────────────
// loadChart
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Loads a chart document and reconstructs the buildLocalDraft() shape.
 * Returns null if the document does not exist.
 */
export async function loadChart(userId: string, chartId: string): Promise<LoadedChart | null> {
  const snap = await getDoc(chartDocRef(userId, chartId));
  if (!snap.exists()) return null;

  const d = snap.data() as Partial<FirestoreChartDoc>;

  if (d.meta?.schemaVersion !== SCHEMA_VERSION) {
    console.warn(
      `[chartApi] schemaVersion mismatch: document has v${d.meta?.schemaVersion},` +
      ` current is v${SCHEMA_VERSION}. Returning anyway -- applyLocalDraft handles partial shapes.`
    );
  }

  return {
    ...(d.config ?? {}),
    data:         d.data        ?? [],
    annotations:  d.annotations ?? [],
    _firestoreId: snap.id,
    _meta:        d.meta,
  } as LoadedChart;
}

// ─────────────────────────────────────────────────────────────────────────────
// listCharts
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns metadata for all saved charts, ordered by updatedAt descending.
 * Optionally filtered by chartType.
 */
export async function listCharts(
  userId:     string,
  chartType:  ChartType | null = null,
): Promise<SavedChartMeta[]> {

  let q: Query<DocumentData> = query(
    userChartsCol(userId),
    orderBy('meta.updatedAt', 'desc'),
  );

  if (chartType) {
    q = query(
      userChartsCol(userId),
      where('meta.chartType', '==', chartType),
      orderBy('meta.updatedAt', 'desc'),
    );
  }

  const snap = await getDocs(q);

  return snap.docs.map(d => {
    const meta = (d.data().meta ?? {}) as Partial<FirestoreChartDoc['meta']> & {
      updatedAt?: { toMillis?: () => number };
      createdAt?: { toMillis?: () => number };
    };
    return {
      chartId:          d.id,
      chartType:        (meta.chartType ?? 'unknown') as ChartType | 'unknown',
      title:            meta.title            ?? 'Untitled',
      updatedAt:        meta.updatedAt?.toMillis?.() ?? 0,
      createdAt:        meta.createdAt?.toMillis?.() ?? 0,
      generationSource: (meta.generationSource ?? 'human') as GenerationSource,
    };
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// deleteChart
// ─────────────────────────────────────────────────────────────────────────────

/** Hard-deletes a chart document. Safe if the document doesn't exist. */
export async function deleteChart(userId: string, chartId: string): Promise<void> {
  await deleteDoc(chartDocRef(userId, chartId));
}

// ─────────────────────────────────────────────────────────────────────────────
// updateSignals
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Partial update of the signals map for an existing chart document.
 * Use for setting individual signal fields without overwriting the rest.
 * All fields are optional — only pass what you want to change.
 */
export async function updateSignals(
  userId:  string,
  chartId: string,
  signals: Partial<ChartSignals>,
): Promise<void> {
  // Build dot-notation update object typed as UpdateData for Firestore compatibility
  const updates: Record<string, boolean | number> = {};
  for (const [k, v] of Object.entries(signals)) {
    if (v !== undefined) updates[`signals.${k}`] = v as boolean | number;
  }
  if (Object.keys(updates).length === 0) return;
  await updateDoc(chartDocRef(userId, chartId), updates as Record<string, boolean | number>);
}

// ─────────────────────────────────────────────────────────────────────────────
// markAbandoned
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Sets signals.abandoned = true for charts not opened in 7+ days.
 * Call on mount of HomeChartStudio or the saved charts list — compare
 * meta.updatedAt against Date.now() - 7 * 24 * 60 * 60 * 1000.
 * Lightweight: only runs if the chart hasn't already been marked abandoned.
 */
export async function markAbandoned(userId: string, chartId: string): Promise<void> {
  await updateDoc(chartDocRef(userId, chartId), {
    'signals.abandoned': true,
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// incrementExportCount
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Atomically increments signals.exportCount and sets signals.exported = true.
 * Call when the user clicks Download MP4 or Export JPG.
 */
export async function incrementExportCount(userId: string, chartId: string): Promise<void> {
  await updateDoc(chartDocRef(userId, chartId), {
    'signals.exported':    true,
    'signals.exportCount': increment(1),
  });
}